import React, { Fragment, type JSX } from 'react';
import styled, { css } from 'styled-components';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { SPLIT_CONSIGNMENT } from 'constants/consignmentStates';
import { FontWeight } from 'lib/styles';
import { Main, Text } from 'styles/themeColors';
import { formatDateTime, currencySign } from 'utils/formatters';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import productColumns from 'constants/productColumns';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { SHIPPING } from 'constants/deliveryModes';
import { CONSIGNMENT_TYPES, TEXT_LABELS } from 'constants/order';
import { useCountries } from 'hooks/useCountries';
import BadgePill from 'components/Settings/ProductBadges/BadgePill';
import EditCell from 'components/UI/EditCell';
import OrderSummary from 'common/OrderSummary';
import AffiliateSummary from 'common/AffiliateSummary';
import AffiliateCommissionLine from 'common/AffiliateCommissionLine';
import ConsignmentEntryComments from 'components/Consignments/ConsignmentEntryComments';
import { addonsRow, serialNumberValidator } from 'utils/consignment';
import EditIcon from '@mui/icons-material/Edit';

const BadgeSlot = styled.span`
  align-self: flex-start;
  margin: 0px 0px 10px 0px;
`;

const ProductImage = styled.img<any>`
  cursor: pointer;
  max-height: 96px;
  max-width: 96px;
  margin: 0 auto;
  align-self: baseline;
  object-fit: contain;
`;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  min-height: 18px;
  width: 234px;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

  ${({ deleted }) => deleted && css`
  opacity: 0.5;
`}
`;

const TableData = styled.td<any>`
  text-align: center;
  vertical-align: top;

  &:first-child:not(:only-child):not([rowspan]) {
    text-align: left;
    font-weight: ${FontWeight.bold};
  }

  &:first-child:not(:only-child)[rowspan] {
    text-align: left;
  }

  &:last-child:not(:only-child)[rowspan] {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
    font-weight: ${FontWeight.bold};
  }

  ${({ color }) => color && css`
    color: ${color};
  `}

  ${({ textDecoration }) => textDecoration && css`
    text-decoration: ${textDecoration};
  `}

  ${({ fontSize }) => fontSize && css`
    font-size: ${fontSize};
  `}

  ${({ fontWeight }) => fontWeight && css`
    font-weight: ${fontWeight};
  `}

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;

interface TableValue {
  product: JSX.Element,
  quantity: JSX.Element,
  unitPrice: JSX.Element,
  subTotal: JSX.Element;
  isRequiredAddon?: boolean;
  isOptionalAddon?: boolean;
  afterRowContent?: JSX.Element;
};

interface Props {
  consignmentEntries: ConsignmentEntry[];
  consignmentEntriesAddons?: ConsignmentEntry[];
  isPreparationStep?: boolean;
  totalTax?: number;
  totalSurcharges?: number;
  totalDeliveryCost?: number;
  totalPriceWithTax?: number;
  state?: string;
  showSummary?: boolean;
  subTotal: number;
  deliveryDate?: string;
  isRental?: boolean;
  handleSerialNumberUpdate?: (consignmentEntryId: number, serialNumber: string) => void;
  isSerialNumberLoading?: boolean;
  serialNumbers: any[];
  setSerialNumbers: (value: any[]) => void;
  canEditSerialNumber?: boolean;
  countryId: number;
  totalDiscounts?: number;
};

const ShippingCostRequiredItems = ({
  consignmentEntries,
  consignmentEntriesAddons = [],
  isPreparationStep,
  totalTax,
  totalDeliveryCost,
  totalPriceWithTax,
  totalDiscounts,
  state,
  totalSurcharges,
  showSummary = true,
  subTotal,
  deliveryDate,
  isRental = false,
  handleSerialNumberUpdate,
  serialNumbers,
  setSerialNumbers,
  canEditSerialNumber = true,
  countryId
}: Props) => {

  const { countries } = useCountries();
  const country = countries.find(x => x.id === countryId)?.countryIso!;

  const productColumn = consignmentEntries && consignmentEntries.map((consignmentEntry, index) => {
    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;
    const pricePerUnit = consignmentEntry?.basePrice;
    const serialNumber = serialNumbers?.find(x => x.consignmentEntryId === consignmentEntry.id)?.serialNumber;
    const snError = serialNumbers?.find(x => x.consignmentEntryId === consignmentEntry.id)?.snError;

    const handleSerialNumberSubmit = (value: string) => {
      value = value?.trim();

      const serialNumberObj = serialNumbers?.find(x => x.consignmentEntryId === consignmentEntry.id);
      const serialNumber = serialNumberObj?.serialNumber || '';
      let error = serialNumberValidator(value);

      const mappedSerialNumbers =
        serialNumbers.map(sn =>
          sn.consignmentEntryId === consignmentEntry.id ? { ...sn, serialNumber: value, snError: error } : sn
        );

      setSerialNumbers(mappedSerialNumbers);

      if (serialNumber?.trim() !== value && !error) {
        handleSerialNumberUpdate?.(serialNumberObj.consignmentEntryId, value);
      }
    }

    return (
      <Fragment key={index}>
        <FlexboxContainerComponent flexDirection='row'>
          <FlexboxContainerComponent padding='0 12px 0 0' flexDirection='column'>
            {
              consignmentEntry.badge &&
              <BadgeSlot>
                <BadgePill name={consignmentEntry.badge.name} colorHash={consignmentEntry.badge.colorHash} />
              </BadgeSlot>
            }
            <ProductImage src={consignmentEntry.productImages && consignmentEntry.productImages.length > 0 ? process.env.REACT_APP_MEDIA_URL! + consignmentEntry.productImages[0].path : ''} />
          </FlexboxContainerComponent>
          <FlexboxContainerComponent flexDirection='column'>
            <FlexboxContainerComponent justifyContent='flex-start' margin='0 0 12px' padding='0px 0 0'>
              <TableData deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT} textDecoration={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT ? 'line-through' : 'normal'}>{consignmentEntry.productName}</TableData>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent flexDirection='row'>
              <FlexboxContainerComponent flexDirection='column' gap='6px'>
                <FlexboxContainerComponent><TableColumn>SKU</TableColumn><TableColumn color={Text.primary}>{consignmentEntry.sku}</TableColumn></FlexboxContainerComponent>
                {
                  isRental &&
                  <FlexboxContainerComponent>
                    <TableColumn>Serial number</TableColumn>
                    {
                      canEditSerialNumber ?
                        <TableColumn color={Text.primary}>
                          <EditCell
                            key={consignmentEntry.id.toString()}
                            value={serialNumber!}
                            onChange={(_, __) => { }}
                            columnIndex={1}
                            error={!!snError}
                            padding='0px !important'
                            border='none'
                            showEditIcon
                            onBlurHandler={handleSerialNumberSubmit}
                            minWidth='100px'
                            maxLength={20}
                            errorMessage={snError}
                            borderRadius='6px'
                            emptyCell={false}
                            hideTooltip={false}
                            isTableCell={false}
                          />
                        </TableColumn> :
                        <TableColumn color={Text.primary} style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>{consignmentEntry.serialNumber || 'N/A'}
                          <EditIcon
                            style={{
                              fontSize: 18,
                              margin: '0px 0px 0px 5px',
                              cursor: 'not-allowed',
                              flexShrink: 0
                            }}
                            htmlColor={Text.disabled}
                          /></TableColumn>
                    }
                  </FlexboxContainerComponent>
                }
                {
                  consignmentEntry.selectableAttributes && consignmentEntry.selectableAttributes.map((atr, index) => {
                    const atrValue = consignmentEntry.attributeValues.find(val => val.attributeId === atr.id);
                    return (
                      <FlexboxContainerComponent key={index} gap='2px !important'><TableColumn>{atr.name}</TableColumn><TableColumn color={Text.primary}>{atrValue ? atrValue.name : ''}</TableColumn></FlexboxContainerComponent>
                    )
                  })
                }
                {
                  consignmentEntry.pricebookName && consignmentEntry.pricebookId &&
                  <FlexboxContainerComponent>
                    <TableColumn>Pricebook name / ID</TableColumn>
                    <TableColumn color={Text.primary}>
                      {`${consignmentEntry.pricebookName} / ${consignmentEntry?.priceBook?.PricebookVendorId}`}
                    </TableColumn>
                  </FlexboxContainerComponent>
                }
                <FlexboxContainerComponent><TableColumn>{consignmentEntries[0]?.deliveryMode === SHIPPING ? "Est. ship date" : "Est. pickup date"}</TableColumn><TableColumn color={Text.primary}>{deliveryDate ? formatDateTime(deliveryDate, false, false) : formatDateTime(consignmentEntry.preliminaryDeliveryDate, false, false)}</TableColumn></FlexboxContainerComponent>
                {
                  consignmentEntry?.rentalStartDate && consignmentEntry?.rentalEndDate &&
                  <FlexboxContainerComponent>
                    <TableColumn>Date range</TableColumn>
                    <TableColumn color={Text.primary}>{formatDateTime(consignmentEntry.rentalStartDate, false, false) + " - " + formatDateTime(consignmentEntry.rentalEndDate, false, false)}</TableColumn>
                  </FlexboxContainerComponent>
                }
                {isRental && pricePerUnit &&
                  <FlexboxContainerComponent>
                    <TableColumn>Price rate</TableColumn>
                    <TableColumn color={Main.brandDarkest} fontWeight={FontWeight.bold}>{`${currencySign(country, pricePerUnit!)} / ${unitOfMeasurement}`}</TableColumn>
                  </FlexboxContainerComponent>
                }
                {
                  consignmentEntry.affiliateCode && consignmentEntry.commissionPercentage != null &&
                  <AffiliateCommissionLine
                    commissionPercentage={consignmentEntry.commissionPercentage}
                    commissionAmount={consignmentEntry.commissionAmount || 0}
                    customerAffiliate={consignmentEntry.customerAffiliate}
                    country={country}
                  />
                }
              </FlexboxContainerComponent>
            </FlexboxContainerComponent>
          </FlexboxContainerComponent>
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const quantityColumn = consignmentEntries && consignmentEntries.map(consignmentEntry => {
    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end' >
          <TableData deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{consignmentEntry.quantity}</TableData>
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const unitPriceColumn = consignmentEntries && consignmentEntries.map(consignmentEntry => {
    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          {
            consignmentEntry.discountedPrice !== consignmentEntry.basePrice
              ? <TableData color={Main.error} deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, consignmentEntry.discountedPrice)}</TableData>
              : <TableData deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, consignmentEntry.basePrice)}</TableData>
          }
        </FlexboxContainerComponent>
      </Fragment>
    )
  });

  const subTotalColumn = consignmentEntries && consignmentEntries.map(consignmentEntry => {
    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;

    return (
      <Fragment key={consignmentEntry.id}>
        <FlexboxContainerComponent justifyContent='flex-end'>
          <TableData deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{currencySign(country, consignmentEntry.totalPrice)}</TableData>
        </FlexboxContainerComponent>

        {
          consignmentEntry.surcharge > 0
            ?
            <FlexboxContainerComponent justifyContent='flex-end' padding='16px 0 0 '>
              <TableData rowSpan={2} deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>+{currencySign(country, consignmentEntry.surcharge)} <br /> surcharge</TableData>
            </FlexboxContainerComponent>
            : <TableData>&nbsp;</TableData>
        }

        {
          isRental && consignmentEntry.plannedUsage > 0 &&
          <FlexboxContainerComponent justifyContent='flex-end' padding='16px 0 0 '>
            <TableData fontWeight={FontWeight.bold} x={2} deleted={consignmentEntry.cancelQuantity > 0 && state !== SPLIT_CONSIGNMENT}>{`${TEXT_LABELS.PLANNED_USAGE}: ${consignmentEntry.plannedUsage} ${unitOfMeasurement}`}</TableData>
          </FlexboxContainerComponent>
        }

      </Fragment>
    )
  });

  const data: TableValue[] = [];

  consignmentEntries.forEach((c, index) => {
    const addonsEntries = consignmentEntriesAddons.filter(x => x.parentEntryNumber === c.entryNumber);
    let requiredAddons = addonsEntries.filter(x => x.requiredAddon);
    let optionalAddons = addonsEntries.filter(x => !x.requiredAddon);

    const requiredAddonsFragmentRows = addonsRow(requiredAddons, true, state!, country!);
    const optionalAddonsFragmentRows = addonsRow(optionalAddons, false, state!, country!);

    data.push({
      product: productColumn[index],
      quantity: quantityColumn[index],
      unitPrice: unitPriceColumn[index],
      subTotal: subTotalColumn[index],
      afterRowContent: c.comments && c.comments.length > 0
        ? <ConsignmentEntryComments comments={c.comments} />
        : undefined
    });

    data.push(...requiredAddonsFragmentRows);
    data.push(...optionalAddonsFragmentRows);
  });


  return (
    <>
      <GenericTableWrapper
        columns={productColumns}
        data={data}
        toolbar
        overrideToolbar
        borderBetweenRows={false}
        wrapperWidth="100%"
        maxHeight="800px"
      />
      {showSummary && (
        <OrderSummary
          country={country}
          subTotal={subTotal}
          totalSurcharges={totalSurcharges}
          totalDeliveryCost={totalDeliveryCost}
          totalTax={totalTax}
          totalPriceWithTax={totalPriceWithTax}
          totalDiscounts={totalDiscounts}
        />
      )}
      {consignmentEntries.some(e => !!e.affiliateCode && e.commissionPercentage != null) && (
        <AffiliateSummary
          country={country}
          totalCommission={consignmentEntries.reduce((sum, e) => sum + (e.commissionAmount || 0), 0)}
        />
      )}
    </>
  );
};

export default React.memo(ShippingCostRequiredItems);
